import React, { useContext } from 'react';
import { Modal, Button, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt,
  faClock,
  faUser,
  faPhone,
  faInbox,
  faChevronLeft,
  faChevronRight,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import FalconCloseButton from 'components/common/FalconCloseButton';

const formatDurationFull = minutes => {
  if (!minutes || minutes <= 0) return '0 min';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h < 24) {
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const days = Math.floor(h / 24);
  const remH = h % 24;
  return remH > 0 ? `${days}d ${remH}h` : `${days}d`;
};

const getDurationBadgeVariant = minutes => {
  if (minutes >= 180) return 'danger'; // > 3 horas
  if (minutes >= 60) return 'warning'; // 1 a 3 horas
  return 'primary'; // < 1 hora
};

const InProgressTicketsModal = ({
  show,
  onHide,
  tickets = [],
  isLoading = false,
  page = 1,
  totalPages = 1,
  totalCount = 0,
  onNextPage,
  onPrevPage,
  onGoToPage
}) => {
  const { config: { isDark: contextIsDark } = {} } = useContext(AppContext);
  const isDark = Boolean(
    (typeof document !== 'undefined' && (
      document.documentElement.classList.contains('dark') ||
      document.body.classList.contains('dark') ||
      document.documentElement.getAttribute('data-bs-theme') === 'dark' ||
      document.body.getAttribute('data-bs-theme') === 'dark'
    )) || contextIsDark
  );

  const handleOpenTicket = ticketId => {
    window.open(`/support-engine/ticket/${ticketId}`, '_blank');
  };

  const maxTimeMinutes = tickets.length > 0 ? Math.max(...tickets.map(t => t.iTiempoTranscurridoMinutos || 0)) : 0;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      backdrop="static"
      className="se-in-progress-modal"
      contentClassName={`border-0 shadow-lg overflow-hidden ${isDark ? 'dark__bg-1000' : 'bg-white'}`}
      style={{ zIndex: 1060 }}
    >
      {/* Header */}
      <Modal.Header
        className="py-3 px-4 d-flex align-items-center justify-content-between"
        style={{
          backgroundColor: isDark ? '#16222f' : '#f8fafc',
          borderBottom: `1px solid ${isDark ? '#233142' : '#e2e8f0'}`
        }}
      >
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: '38px',
              height: '38px',
              backgroundColor: isDark ? 'rgba(56, 189, 248, 0.15)' : '#e0f2fe',
              color: isDark ? '#38bdf8' : '#0284c7'
            }}
          >
            <FontAwesomeIcon icon={faTachometerAlt} style={{ fontSize: '1.05rem' }} />
          </div>
          <div>
            <h5 className="modal-title mb-0 fw-bold" style={{ color: isDark ? '#f8fafc' : '#0f172a', fontSize: '1.15rem' }}>
              Tickets en Atención Activa
            </h5>
            <small style={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.80rem' }}>
              Ordenados por mayor tiempo transcurrido en el sistema
            </small>
          </div>
        </div>
        <FalconCloseButton onClick={onHide} className="ms-auto" />
      </Modal.Header>

      {/* Body */}
      <Modal.Body
        className="p-3 p-md-4"
        style={{
          backgroundColor: isDark ? '#121e2d' : '#ffffff',
          color: isDark ? '#f8fafc' : '#0f172a'
        }}
      >
        {/* Barra de métricas rápidas */}
        <div className="d-flex align-items-center gap-3 flex-wrap mb-3">
          <div
            className="px-3 py-2 rounded-3 d-flex align-items-center gap-2"
            style={{
              backgroundColor: isDark ? '#16222f' : '#f8fafc',
              border: `1px solid ${isDark ? '#233142' : '#e2e8f0'}`
            }}
          >
            <span style={{ fontSize: '0.80rem', color: isDark ? '#94a3b8' : '#64748b' }}>Total en proceso:</span>
            <span className="fw-bold" style={{ color: isDark ? '#38bdf8' : '#0284c7', fontSize: '1rem' }}>
              {totalCount || tickets.length}
            </span>
          </div>

          {tickets.length > 0 && (
            <div
              className="px-3 py-2 rounded-3 d-flex align-items-center gap-2"
              style={{
                backgroundColor: isDark ? '#16222f' : '#f8fafc',
                border: `1px solid ${isDark ? '#233142' : '#e2e8f0'}`
              }}
            >
              <FontAwesomeIcon icon={faClock} style={{ color: '#ef4444', fontSize: '0.85rem' }} />
              <span style={{ fontSize: '0.80rem', color: isDark ? '#94a3b8' : '#64748b' }}>Tiempo máximo:</span>
              <span className="fw-bold" style={{ color: '#ef4444', fontSize: '1rem' }}>
                {formatDurationFull(maxTimeMinutes)}
              </span>
            </div>
          )}
        </div>

        {/* Tabla / Lista de Tickets */}
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2" style={{ fontSize: '0.85rem', color: isDark ? '#94a3b8' : '#64748b' }}>
              Cargando tickets en progreso...
            </div>
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-5">
            <div className="mb-2" style={{ color: isDark ? '#475569' : '#94a3b8' }}>
              <FontAwesomeIcon icon={faInbox} size="3x" />
            </div>
            <h6 className="fw-bold mb-1" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
              No hay tickets en progreso actualmente
            </h6>
            <p className="mb-0" style={{ fontSize: '0.82rem', color: isDark ? '#94a3b8' : '#64748b' }}>
              Todos los tickets asignados están al día o resueltos.
            </p>
          </div>
        ) : (
          <div
            className="table-responsive rounded-3"
            style={{
              border: `1px solid ${isDark ? '#233142' : '#e2e8f0'}`,
              maxHeight: '55vh'
            }}
          >
            <table
              className="table align-middle mb-0"
              style={{
                fontSize: '0.85rem',
                backgroundColor: isDark ? '#121e2d' : '#ffffff',
                color: isDark ? '#f8fafc' : '#0f172a'
              }}
            >
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  backgroundColor: isDark ? '#16222f' : '#f8fafc',
                  borderBottom: `1px solid ${isDark ? '#233142' : '#e2e8f0'}`
                }}
              >
                <tr style={{ color: isDark ? '#94a3b8' : '#475569', fontSize: '0.72rem', letterSpacing: '0.04em' }}>
                  <th style={{ width: '95px' }} className="ps-3 py-2 text-uppercase fw-semibold">Ticket</th>
                  <th style={{ minWidth: '170px' }} className="py-2 text-uppercase fw-semibold">Cliente</th>
                  <th style={{ minWidth: '140px' }} className="py-2 text-uppercase fw-semibold">Asesor Asignado</th>
                  <th style={{ minWidth: '200px' }} className="py-2 text-uppercase fw-semibold">Asunto / Detalle</th>
                  <th style={{ minWidth: '130px' }} className="text-center py-2 text-uppercase fw-semibold">Tiempo</th>
                  <th style={{ width: '90px' }} className="text-center pe-3 py-2 text-uppercase fw-semibold">Acción</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t, idx) => {
                  const durationMinutes = t.iTiempoTranscurridoMinutos || 0;
                  const badgeVariant = getDurationBadgeVariant(durationMinutes);
                  const isTopOne = idx === 0;

                  return (
                    <tr
                      key={t.idTicket}
                      className="se-in-progress-table-row"
                      style={{
                        borderBottom: `1px solid ${isDark ? '#1e293b' : '#f1f5f9'}`,
                        cursor: 'pointer'
                      }}
                      onClick={() => handleOpenTicket(t.idTicket)}
                      title={`Clic para abrir el Ticket #${t.idTicket} en nueva pestaña`}
                    >
                      {/* Ticket ID */}
                      <td className="ps-3 py-3">
                        <div className="d-flex align-items-center gap-1">
                          <span className="fw-bold" style={{ color: isDark ? '#38bdf8' : '#0284c7' }}>
                            #{t.idTicket}
                          </span>
                          {isTopOne && (
                            <Badge bg="danger" className="ms-1" style={{ fontSize: '0.62rem', padding: '0.2rem 0.4rem' }}>
                              Máx
                            </Badge>
                          )}
                        </div>
                      </td>

                      {/* Cliente */}
                      <td
                        className="py-3"
                        onClick={e => {
                          if (t.idClienteAkasia) {
                            e.stopPropagation();
                            window.open(`/clientes/detalle/${t.idClienteAkasia}`, '_blank');
                          }
                        }}
                      >
                        {t.idClienteAkasia ? (
                          <a
                            href={`/clientes/detalle/${t.idClienteAkasia}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="fw-bold text-decoration-none d-inline-flex align-items-center gap-1"
                            style={{ color: isDark ? '#38bdf8' : '#0284c7' }}
                            title={`Ver perfil del cliente #${t.idClienteAkasia} en nueva pestaña`}
                          >
                            <span>{t.vCustomerName || `Cliente #${t.idClienteAkasia}`}</span>
                            <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.65rem', opacity: 0.8 }} />
                          </a>
                        ) : (
                          <div className="fw-semibold" style={{ color: isDark ? '#f8fafc' : '#0f172a' }}>
                            {t.vCustomerName || 'Cliente sin nombre'}
                          </div>
                        )}
                        {t.vTelefono && (
                          <div className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: '0.75rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                            <FontAwesomeIcon icon={faPhone} style={{ fontSize: '0.70rem' }} />
                            <span>{t.vTelefono}</span>
                          </div>
                        )}
                      </td>

                      {/* Asesor Asignado */}
                      <td className="py-3">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                              width: '28px',
                              height: '28px',
                              backgroundColor: isDark ? 'rgba(56, 189, 248, 0.15)' : '#eff6ff',
                              color: isDark ? '#38bdf8' : '#2563eb',
                              fontSize: '0.74rem'
                            }}
                          >
                            <FontAwesomeIcon icon={faUser} />
                          </div>
                          <span className="fw-medium" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                            {t.vNombreUsuarioAsignado || 'Sin asignar'}
                          </span>
                        </div>
                      </td>

                      {/* Asunto / Preview */}
                      <td className="py-3">
                        <div className="text-truncate" style={{ maxWidth: '280px' }} title={t.vAsunto || 'Sin asunto'}>
                          <span style={{ color: isDark ? '#cbd5e1' : '#334155' }}>
                            {t.vAsunto || <span className="text-muted fst-italic">Sin asunto</span>}
                          </span>
                        </div>
                      </td>

                      {/* Tiempo Transcurrido */}
                      <td className="text-center py-3">
                        <div
                          className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-2 fw-bold"
                          style={{
                            fontSize: '0.78rem',
                            backgroundColor:
                              badgeVariant === 'danger'
                                ? isDark ? 'rgba(239, 68, 68, 0.18)' : '#fee2e2'
                                : badgeVariant === 'warning'
                                  ? isDark ? 'rgba(245, 158, 11, 0.18)' : '#fef3c7'
                                  : isDark ? 'rgba(56, 189, 248, 0.15)' : '#e0f2fe',
                            color:
                              badgeVariant === 'danger'
                                ? (isDark ? '#f87171' : '#ef4444')
                                : badgeVariant === 'warning'
                                  ? (isDark ? '#fbbf24' : '#d97706')
                                  : (isDark ? '#38bdf8' : '#0284c7')
                          }}
                        >
                          <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.72rem' }} />
                          <span>{formatDurationFull(durationMinutes)}</span>
                        </div>
                      </td>

                      {/* Acción: Botón para abrir el ticket */}
                      <td className="text-center pe-3 py-3" onClick={e => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant={isDark ? 'outline-info' : 'outline-primary'}
                          className="d-inline-flex align-items-center gap-1 py-1 px-2"
                          style={{ fontSize: '0.74rem', borderRadius: '6px' }}
                          onClick={() => handleOpenTicket(t.idTicket)}
                          title={`Abrir ticket #${t.idTicket} en nueva pestaña`}
                        >
                          <span>Abrir</span>
                          <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.62rem' }} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>

      {/* Footer */}
      <Modal.Footer
        className="py-2 px-4"
        style={{
          backgroundColor: isDark ? '#16222f' : '#f8fafc',
          borderTop: `1px solid ${isDark ? '#233142' : '#e2e8f0'}`
        }}
      >
        <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-2">
          {/* Contador de registros */}
          <div style={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.80rem' }}>
            Mostrando {tickets.length} {totalCount > 0 ? `de ${totalCount}` : ''} tickets en atención
          </div>

          {/* Controles de paginación con flechas <> */}
          {totalPages > 1 && (
            <div className="d-flex align-items-center gap-2">
              <Button
                variant={isDark ? 'dark' : 'light'}
                size="sm"
                className="d-flex align-items-center justify-content-center p-0"
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '6px',
                  border: `1px solid ${isDark ? '#334155' : '#cbd5e1'}`,
                  color: isDark ? '#e2e8f0' : '#1e293b',
                  backgroundColor: isDark ? '#1e293b' : '#ffffff'
                }}
                onClick={onPrevPage}
                disabled={page <= 1 || isLoading}
                title="Página anterior"
              >
                <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '0.75rem' }} />
              </Button>

              <span style={{ fontSize: '0.80rem', fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b', minWidth: '60px', textAlign: 'center' }}>
                {page} / {totalPages}
              </span>

              <Button
                variant={isDark ? 'dark' : 'light'}
                size="sm"
                className="d-flex align-items-center justify-content-center p-0"
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '6px',
                  border: `1px solid ${isDark ? '#334155' : '#cbd5e1'}`,
                  color: isDark ? '#e2e8f0' : '#1e293b',
                  backgroundColor: isDark ? '#1e293b' : '#ffffff'
                }}
                onClick={onNextPage}
                disabled={page >= totalPages || isLoading}
                title="Página siguiente"
              >
                <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '0.75rem' }} />
              </Button>
            </div>
          )}

          {/* Botón Cerrar */}
          <Button
            size="sm"
            onClick={onHide}
            style={{
              backgroundColor: isDark ? '#233142' : '#e2e8f0',
              borderColor: isDark ? '#334155' : '#cbd5e1',
              color: isDark ? '#f8fafc' : '#334155'
            }}
          >
            Cerrar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default InProgressTicketsModal;
